// slices/chat/hydratedTurn.ts
//
// The turn the chat slice rebuilds for a message read back from history. Moved
// out of `chatSlice.ts`, which is at its size limit.

import type { ChatTurn } from './chatSlice';
import type { ChatHydratedMessage } from './chatSlicePayloads';

/**
 * The finished turn behind a restored answer (ORA-604, ORA-606).
 *
 * History has no turns of its own — the client ids the stream used are gone —
 * so a message that carries a trail or its chips gets a turn keyed by the
 * MESSAGE id, which is the key `ChatMessageItem` looks a turn up by. It is
 * built `done` and empty of everything a live turn accumulates: what the
 * student can see of it is exactly what the server persisted.
 */
export const hydratedTurn = (
  conversationId: string,
  message: ChatHydratedMessage,
  userMessageId: string
): ChatTurn => ({
  id: message.id,
  serverTurnId: null,
  conversationId,
  status: 'done',
  statusLabel: null,
  statusTrail: message.activity ? [...message.activity.statusTrail] : [],
  // No `startedAt`: the turn ran in another session, so there is no client
  // clock to measure it against — the persisted duration is the only one.
  userMessageId,
  assistantMessageId: message.id,
  regenerateOf: null,
  supersededMessageIds: [],
  depth: null,
  envelope: null,
  protocol: null,
  model: null,
  lastSeq: null,
  seqGapDetected: false,
  createdConversation: false,
  toolCallIds: [],
  toolCalls: {},
  blockIds: (message.blocks ?? []).map((block) => block.id),
  followups: message.followups ?? [],
  actionProposal: message.actionProposal
    ? { ...message.actionProposal, turnId: message.id }
    : null,
  error: null,
  traceId: null,
  steps: null,
  // A persisted zero means the server had no duration to record, which is not
  // the same as "it took no time": the summary then counts steps alone.
  ms: message.activity && message.activity.ms > 0 ? message.activity.ms : null,
});

/** True when a turn is one this reducer rebuilt from history, not a live one. */
export const isHydratedTurn = (turn: ChatTurn | undefined, messageId: string) =>
  turn !== undefined &&
  turn.id === messageId &&
  turn.assistantMessageId === messageId;
